import {
  filterClientSelectOptions,
  getClientSelectOptionLabel,
  renderClientSelectOption,
} from '@src/clients/ClientSelectOption';
import type { ClientSummary } from '@src/clients/domain/Client';
import { useClientsSummary } from '@src/clients/hooks/useClientsSummary';
import { UnclearableAutocomplete } from '@src/common/components/Autocomplete';
import { CircularProgress } from '@src/common/components/CircularProgress';
import { TextInput } from '@src/common/components/TextInput';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

export interface BillToDropdownProps {
  clientId?: string;
  onChange: (client: ClientSummary) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
}

export const BillToDropdown = ({
  clientId,
  disabled,
  size,
  label,
  onChange,
}: BillToDropdownProps) => {
  const { t } = useTranslation();
  const {
    clientList: { clients },
    isLoading: loadingClients,
  } = useClientsSummary();

  const selectedClient = clients.find((client) => client.id === clientId);

  return (
    <UnclearableAutocomplete
      key={`maybe-loading-billto-${loadingClients}`}
      getOptionLabel={getClientSelectOptionLabel}
      filterOptions={filterClientSelectOptions}
      renderOption={renderClientSelectOption}
      options={clients.filter((client) => !!client.paymentMethod)}
      loading={loadingClients}
      value={selectedClient ?? ''}
      disabled={disabled}
      size={size}
      onChange={(_, value) => {
        onChange(value as ClientSummary);
      }}
      renderInput={(params) => (
        <TextInput
          {...params}
          label={label ?? t('fields.billTo')}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {loadingClients ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </Fragment>
              ),
            },
          }}
        />
      )}
    />
  );
};
